/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, HostBinding, Inject, OnInit, Optional, ViewChild } from '@angular/core';
import { FormControl, NgModel } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { createDialogRoute, getDialogInitData, setEntityField } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { AuthService, Permission } from '@gh/core-auth';
import { TranslationParams, TranslationService } from '@gh/core-mls';
import {
  DialogInput,
  NotificationService,
  requiredFormValidator,
  WaitingService,
} from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { Observable } from 'rxjs';

export interface EntityInputDialogConfig<T> {
  id?: ID;
  title?: string;
  titleId?: string;
  titleParams?: TranslationParams;
  message?: string;
  messageId?: string;
  messageParams?: TranslationParams;
  placeholder?: string;
  placeholderId?: string;
  placeholderParams?: TranslationParams;
  readOnly?: boolean;
  permissionEdit?: Permission;
  permissionView?: Permission;
  required?: boolean;
  maxLength?: number;
  field: keyof T;
  showTextArea?: boolean;
  minHeight?: number;
  entity?: T;
  saveButtonLabelId?: string;
  saveButtonLabel?: string;

  onInit?(): Observable<T> | T;

  onSave(entity: T): Observable<T>;
}

@Component({
  standalone: false,
  selector: 'gh-entity-input-dialog',
  templateUrl: 'entity-input-dialog.component.html',
  styleUrls: ['entity-input-dialog.component.scss'],
})
export class EntityInputDialogComponent<T> implements DialogInput<EntityInputDialogConfig<T>>, OnInit {
  input: EntityInputDialogConfig<T>;

  title: string;
  message: string;
  placeholder: string;
  entity: T;
  control: FormControl;
  readOnly: boolean;
  saveButtonLabel: string;

  defaultSaveButtonLabelId = 'lbl_save';

  @ViewChild('valueInput', { static: false }) valueInput: NgModel;
  @ViewChild('valueArea', { static: false }) valueArea: NgModel;

  get hasMessage(): boolean {
    return isNotNil(this.message);
  }

  @HostBinding('style.minHeight.px')
  get minHeight(): number {
    return this.input.minHeight || 490;
  }

  constructor(private notificationService: NotificationService,
              private translationService: TranslationService,
              private authService: AuthService,
              public dialogRef: MatDialogRef<EntityInputDialogComponent<T>>,
              @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any,
              private waitingService: WaitingService) {

  }

  ngOnInit(): void {
    if (this.input.readOnly) {
      this.readOnly = true;
    } else if (this.input.permissionEdit) {
      this.readOnly = !this.authService.hasPermission(this.input.permissionEdit);
    } else {
      this.readOnly = false;
    }

    if (this.input.titleId) {
      this.title = this.translationService.get(this.input.titleId, this.input.titleParams);
    } else if (this.input.title) {
      this.title = this.input.title;
    }

    if (this.input.messageId) {
      this.message = this.translationService.get(this.input.messageId, this.input.messageParams);
    } else if (this.input.message) {
      this.message = this.input.message;
    }

    if (this.input.placeholderId) {
      this.placeholder = this.translationService.get(this.input.placeholderId, this.input.placeholderParams);
    } else if (this.input.placeholder) {
      this.placeholder = this.input.placeholder;
    }

    if (this.input.entity) {
      this.initEntity(this.input.entity);
    } else if (this.input.onInit) {
      const entity = this.input.onInit();

      if (entity instanceof Observable) {
        entity.pipe(
          this.waitingService.operator<T>())
          .subscribe((nextEntity) => this.initEntity(nextEntity));
      } else {
        this.initEntity(entity);
      }
    } else if (getDialogInitData(this.dialogData)) {
      this.initEntity(getDialogInitData(this.dialogData));
    } else {
      throw new Error('EntityInputDialogComponent: either onInit callback (via config) ' +
        'or init parameter (via dialog resolve) should be provided');
    }

    if (this.input.saveButtonLabelId) {
      this.saveButtonLabel = this.translationService.get(this.input.saveButtonLabelId);
    } else if (this.input.saveButtonLabel) {
      this.saveButtonLabel = this.input.saveButtonLabel;
    } else {
      this.saveButtonLabel = this.translationService.get(this.defaultSaveButtonLabelId);
    }
  }

  onYes(): void {
    this.entity = setEntityField(this.entity, this.input.field, this.control.value);
    if (this.input.onSave) {
      this.input.onSave(this.entity).pipe(
        this.waitingService.operator<T>())
        .subscribe(
          (entity) => {
            this.dialogRef.close(entity);
          },
          this.notificationService.processAll,
        );
    } else {
      this.dialogRef.close(this.entity);
    }
  }

  onCancel(): void {
    this.dialogRef.close(undefined);
  }

  private initEntity(entity: T): void {
    this.entity = entity;
    this.control = new FormControl(
      {
        value: entity[this.input.field],
        disabled: this.readOnly,
      },
      this.input.required ? requiredFormValidator : void 0);
  }

}

export const entityInputDialogRoute = createDialogRoute({
  component: EntityInputDialogComponent,
});
